import {uploadData} from 'aws-amplify/storage';
import {retry, sanitizeS3Metadata, sanitizeS3PathSegment, withTimeout} from './utils'; // Assume we have a utility function for retrying operations
import Logger from '@/logger';
import packageInfo from "../package.json";
import { fetchAuthSession } from 'aws-amplify/auth';
import { restPost } from '@/restApiFallback';
import { reportDeviceEvent } from '@/deviceEvents';
import { reportForensicEvent } from '@/forensicEvents';
import { isLiveDemoId } from '@/liveDemo/flag';
const logger = new Logger({screen: 'backgroundUploader'});

let dbInstance = null;
// Reentrancy guard. On reconnect several triggers fire almost together — the 15 s interval,
// the 'photo-saved' handler, and the online resync — each calling uploadPendingPhotos. Without
// this, two runs read the same pending set and upload the SAME photos in parallel (Adrián saw
// "trying to upload photos again" on already-processed ones). Overlapping calls now no-op.
let uploadInProgress = false;

// Upload cycles (15s each) to wait for a photo's order to appear in IndexedDB before
// purging the photo as an orphan: 20 cycles ≈ 5 minutes of grace.
const ORPHAN_PURGE_CYCLES = 20;

// Longest one S3 PUT attempt may run before it is cancelled and counted as a failed attempt.
// Uploads are sequential, and Amplify's uploadData has no timeout of its own: stalled PUTs took
// 10-12 minutes to fail and held every other order's photos behind them. A timed-out attempt goes
// through the same retry/backoff and progress reset as any other failed attempt.
export const UPLOAD_ATTEMPT_TIMEOUT_MS = 90 * 1000;

/** True while an upload cycle is running. The socket watchdog reads it to find a quiet moment. */
export function isUploadInProgress() {
    return uploadInProgress;
}

// Settles when the startup progress reset below has finished. The first cycle awaits it, so a
// stale partial record is never read before it has been zeroed.
let startupReset = Promise.resolve();

export function setupBackgroundUploader(db) {
    if (!db) {
        throw new Error('Database instance is required');
    }
    dbInstance = db;
    // A restart (or a killed Chrome) mid-PUT leaves transferredBytes/totalBytes non-zero on the
    // record, because the catch that zeroes them never ran. Nothing is uploading at startup, so
    // any partial progress found now is stale: reset it. Skipped if a cycle is already running
    // (a re-login re-calls this in the same page; that cycle owns the progress it is writing).
    startupReset = uploadInProgress ? Promise.resolve() : resetStaleUploadProgress()
        .catch((error) => logger.error('Could not reset stale upload progress', {error: error}));
}

async function resetStaleUploadProgress() {
    return new Promise((resolve, reject) => {
        const transaction = dbInstance.transaction('photos', 'readwrite');
        const store = transaction.objectStore('photos');
        const cursorRequest = store.openCursor();
        let reset = 0;
        cursorRequest.onsuccess = (event) => {
            const cursor = event.target.result;
            if (!cursor) return;
            const photo = cursor.value;
            if (photo.transferredBytes || photo.totalBytes || photo.percentageUploaded) {
                photo.percentageUploaded = 0;
                photo.transferredBytes = 0;
                photo.totalBytes = 0;
                cursor.update(photo);
                reset += 1;
            }
            cursor.continue();
        };
        cursorRequest.onerror = (event) => reject(event.target.error);
        transaction.oncomplete = () => {
            if (reset > 0) logger.info(`Reset stale upload progress on ${reset} pending photo(s)`);
            resolve(reset);
        };
        transaction.onabort = () => reject(transaction.error || new Error('Reset transaction aborted'));
    });
}

async function handleAuthError() {
    try {
        // Try to refresh the session
        const session = await fetchAuthSession({ forceRefresh: true });
        
        // Check if we actually have valid tokens
        if (session?.tokens?.idToken && session?.tokens?.accessToken) {
            logger.success('Successfully refreshed authentication session with valid tokens');
            return true;
        } else {
            logger.warning('Authentication session refreshed but no valid tokens found');
            // Dispatch session expired event
            window.dispatchEvent(new CustomEvent('sessionExpired'));
            return false;
        }
    } catch (error) {
        logger.error('Failed to refresh authentication session', {error: error});
        // Dispatch session expired event
        window.dispatchEvent(new CustomEvent('sessionExpired'));
        return false;
    }
}
async function uploadPendingPhotos(restaurantId, deviceId) {
    if (uploadInProgress) {
        logger.console('Upload cycle already running — skipping overlapping trigger');
        return;
    }
    uploadInProgress = true;
    try {
        await startupReset;
        const photos = await getPendingPhotos();

        // Every pending record is attempted, including one with partial progress. This module is
        // the only caller of uploadData and uploadInProgress above allows one cycle at a time, so a
        // record with partial progress while no cycle is running is a PUT that was cut off (wifi
        // lost, Chrome killed, restart), never one in flight. The old filter skipped such a record
        // in every later cycle: never retried, so hasPendingPhotos(orderId) stayed true and the
        // queued analysis expired unannounced. trackStuckUploads still sees every pending record
        // and prunes keys that are no longer pending on its own, which is why the early return
        // below does not clear the map.
        trackStuckUploads(photos);

        if (photos.length === 0) {
            logger.console('No pending photos to upload');
            return;
        }

        logger.console(`Found ${photos.length} pending photos to upload`);

        for (const photo of photos) {
            try {
                await processPhoto(photo, restaurantId, deviceId);
            } catch (error) {
                const authErrors = ['NotAuthorizedException', 'UnauthorizedException', 'NoCredentials'];
                if (authErrors.includes(error.name)) {
                    logger.warning('Authentication error, attempting to refresh session');
                    const sessionRefreshed = await handleAuthError();
                    if (sessionRefreshed) {
                        // Retry this photo if session was refreshed
                        try {
                            await processPhoto(photo, restaurantId, deviceId);
                            continue;
                        } catch (retryError) {
                            logger.error(`Failed to process photo ${photo.key} after session refresh:`, {error: retryError});
                            // Mark as uploaded to skip it in future attempts
                            //await markPhotoAsUploaded(photo.key);
                            //logger.warning(`Marked problematic photo ${photo.key} as uploaded to skip it`);
                        }
                    }
                    // Stop processing if we couldn't refresh the session
                    return;
                }
                
                logger.error(`Failed to process photo ${photo.key}:`, {error: error});
                // Mark as uploaded to skip it in future attempts
                //await markPhotoAsUploaded(photo.key);
                //logger.warning(`Marked problematic photo ${photo.key} as uploaded to skip it`);
                // Continue with next photo for other types of errors
            }
        }
    } catch (error) {
        logger.error(`Error in uploadPendingPhotos:`, {error: error});
    } finally {
        uploadInProgress = false;
    }
}


// Stuck-upload detection: photos are keyed the first time we see them pending;
// if any photo is still pending past the threshold, report upstream (the
// deviceEvents module throttles to 1/min, so this can't flood). In-memory on
// purpose — a reload restarts the 5-min window, and a genuinely stuck queue
// stays stuck long enough to re-trigger.
const STUCK_UPLOAD_THRESHOLD_MS = 5 * 60 * 1000;
const pendingFirstSeen = new Map();

function trackStuckUploads(photos) {
    const now = Date.now();
    const currentKeys = new Set(photos.map(photo => photo.key));
    for (const key of pendingFirstSeen.keys()) {
        if (!currentKeys.has(key)) pendingFirstSeen.delete(key);
    }
    let oldestAgeMs = 0;
    for (const photo of photos) {
        if (!pendingFirstSeen.has(photo.key)) pendingFirstSeen.set(photo.key, now);
        oldestAgeMs = Math.max(oldestAgeMs, now - pendingFirstSeen.get(photo.key));
    }
    if (oldestAgeMs >= STUCK_UPLOAD_THRESHOLD_MS) {
        reportDeviceEvent('upload_stuck', {
            pending_count: photos.length,
            oldest_age_s: Math.round(oldestAgeMs / 1000),
        });
    }
}

async function processPhoto(photo, restaurantId, deviceId) {
    // Live sales demo: a demo photo never reaches S3 or /notify-photo (src/liveDemo/live.js replays it
    // locally). getPendingPhotos already leaves it out; this is the second lock.
    if (isLiveDemoId(photo.order)) return;
    if (!photo.order) {
        logger.error(`Photo ${photo.key} has no order property. Marking as uploaded and skipping.`);
        await markPhotoAsUploaded(photo.key);
        return;
    }
    const order = await getOrderByKey(photo.order);
    if (!order) {
        // The order may simply not have synced yet, so give it a grace period — but a
        // photo whose order never appears would otherwise be retried every 15s forever
        // (a single stuck photo once produced ~200k identical PostHog events). Track the
        // attempts on the record and purge it after the grace period, with ONE warning.
        const orphanCycles = (photo.orphanCycles || 0) + 1;
        if (orphanCycles >= ORPHAN_PURGE_CYCLES) {
            await deletePhotoRecord(photo.key);
            // A discard is not an arrival. Announcing it as `photoUploaded` told the counter the
            // truth (the record did leave) but told analysisQueue a lie (photos are in S3, run the
            // comparison). Two events, so a listener can tell the difference.
            window.dispatchEvent(new CustomEvent('photoDiscarded', { detail: { photoKey: photo.key } }));
            reportDeviceEvent('photo_orphan_purged', {
                photo_key: photo.key, order_key: photo.order, cycles: orphanCycles,
            });
            logger.warning('Orphan photo purged: order never appeared in IndexedDB', {
                photoKey: photo.key, orderKey: photo.order, cycles: orphanCycles,
            });
        } else {
            await setPhotoOrphanCycles(photo.key, orphanCycles);
            logger.console(`Order not found for photo ${photo.key} (cycle ${orphanCycles}/${ORPHAN_PURGE_CYCLES}). Skipping.`);
        }
        return;
    }

    // Get app version
    const appVersion = packageInfo.versionInternal;

    const metadata = buildMetadata(order, appVersion, restaurantId);
    const path = buildPhotoPath(restaurantId, deviceId, order, photo);

    // POST to the Trigger API to start processing the photo in AWS Step Function
    try {
        // Define API Trigger payload
        const trigger_payload = {
            "image-key": path,
            "restaurant-id": restaurantId,
            "order-id": order.orderId || null,
            "delivery-id": order.deliveryId || null,
            "order-analysis-id": order.orderAnalysis || null,
            "tag": "Default",
            "ticket-id": order.ticketId || null,
            "weight-kg": null,
            "app-version": appVersion,
        }

        logger.info(`Photo Key: ${path} - POST to trigger API`, {trigger_payload: trigger_payload})

        const result = await restPost({
            apiName: 'WEBAPP-API',
            path: '/notify-photo',
            options: {
                body: trigger_payload,
            }
        });
        if (result.result === 'success') {
            logger.success(`Photo Key: ${path} - Successful trigger API`, {result: result})
        } else {
            logger.error(`Photo Key: ${path} - Error in trigger API response`, {result: result})
        }
    } catch (error) {
        logger.error(`Photo Key: ${path} - Error in Trigger API`, {error: error});
    }

    const fxUploadStart = Date.now();
    reportForensicEvent('upload_start', {
        order_id: order.orderId || null,
        photo_key: photo.key,
        s3_path: path,
        interaction_id: photo.interactionId || null,
        bytes: photo.image?.size ?? null,
    });
    try {
        logger.info(`Photo Key: ${path} - Uploading photo...`, {
            metadata: metadata,
            path: path,
            useAccelerateEndpoint: true,
            contentType: 'image/jpeg',
        })
        await retry(async () => {
            // Set when this attempt is abandoned, so progress callbacks still in flight from the
            // cancelled task cannot write partial bytes over the reset below.
            let attemptOver = false;
            const task = uploadData({
                path: path,
                data: photo.image,
                options: {
                    useAccelerateEndpoint: true,
                    contentType: 'image/jpeg',
                    metadata,
                    onProgress: async (event) => {
                        if (attemptOver) return;
                        const percentLoaded = event.transferredBytes && event.totalBytes 
                            ? Math.round((event.transferredBytes / event.totalBytes) * 100) 
                            : 0;
                        logger.console(`Progress for ${photo.key}: ${percentLoaded}% (${event.transferredBytes || 0}/${event.totalBytes || 0} bytes)`);
                        
                        try {
                            await updatePhotoUploadProgress(photo.key, percentLoaded, event.transferredBytes, event.totalBytes);
                        } catch (error) {
                            logger.console(`Failed to update upload progress in database: ${error}`);
                        }
                    },
                },
            });
            let result;
            try {
                result = await withTimeout(task.result, UPLOAD_ATTEMPT_TIMEOUT_MS, 'S3 upload');
            } catch (attemptError) {
                attemptOver = true;
                if (attemptError?.name === 'TimeoutError') {
                    // Stop the stalled PUT so it frees its connection; a late result is ignored.
                    try { task.cancel?.(); } catch (cancelError) { logger.console('Could not cancel stalled upload', cancelError); }
                }
                throw attemptError;
            }
            logger.success(`Photo Key: ${result.path} - Upload successful.`);
        }, 3, 1000); // Retry up to 3 times with 1 second delay

        await markPhotoAsUploaded(photo.key);
        reportForensicEvent('upload_success', {
            order_id: order.orderId || null,
            photo_key: photo.key,
            s3_path: path,
            duration_ms: Date.now() - fxUploadStart,
            bytes: photo.image?.size ?? null,
        });
    } catch (error) {
        // As the photo is not uploaded, we need to setup the upload progress to 0 so it can be uploaded again
        await updatePhotoUploadProgress(photo.key, 0, 0, 0);
        logger.error(`Photo Key: ${path} - Failed to upload photo`, {error: error});
        reportForensicEvent('upload_failed', {
            order_id: order.orderId || null,
            photo_key: photo.key,
            s3_path: path,
            interaction_id: photo.interactionId || null,
            duration_ms: Date.now() - fxUploadStart,
            bytes: photo.image?.size ?? null,
            error: error?.message ?? `${error}`,
            // 'timeout' = the last attempt exceeded UPLOAD_ATTEMPT_TIMEOUT_MS and was cancelled.
            reason: error?.name === 'TimeoutError' ? 'timeout' : 'error',
        });
        throw error; // Rethrow to be caught in the main loop
    }
}

function buildMetadata(order, appVersion, restaurantId) {
    // Metadata travels as `x-amz-meta-*` HTTP headers signed by SigV4, so a value with
    // a non-ASCII or control character (accented restaurant name, symbol in a POS-supplied
    // order id) makes the request fail outright — the photo is retried three times and then
    // never reaches the bucket. sanitizeS3Metadata also drops the null entries, which are
    // meaningless as headers, and narrows the charset to what the downstream trigger lambda
    // can safely reuse as a Step Functions execution name.
    return sanitizeS3Metadata({
        'app-version': appVersion,
        'order-id': order.orderId || null,
        'order-analysis-id': order.orderAnalysis || null,
        'restaurant-id': restaurantId,
        'tag': 'Default',
        'weight-kg': null,
    });
}

function buildPhotoPath(restaurantId, deviceId, order, photo) {
    const formattedDate = new Date(photo.timestamp).toISOString().split('T')[0].split('-').reverse().join('-');
    const formattedTime = new Date(photo.timestamp).toISOString().split('T')[1].split('.')[0].replace(/:/g, '-');
    // restaurantId comes from the backend and deviceId from the Cognito username, so either
    // may contain characters that are not S3-safe (spaces, accents, #, %, ?, ...). Sanitize
    // each path segment so a bad character never produces an invalid object key that silently
    // fails the upload. The uuid (photo.key), date and time pieces are already S3-safe.
    const safeRestaurantId = sanitizeS3PathSegment(restaurantId);
    const safeDeviceId = sanitizeS3PathSegment(deviceId);
    return `${safeRestaurantId}/${safeDeviceId}/${photo.key}_${formattedDate}_${formattedTime}.jpg`;
}

async function getPendingPhotos() {
    return new Promise((resolve, reject) => {
        if (!dbInstance) {
            reject(new Error('Database not initialized'));
            return;
        }
        const transaction = dbInstance.transaction('photos', 'readonly');
        const store = transaction.objectStore('photos');
        const request = store.getAll();

        request.onsuccess = (event) => resolve(
            // Live sales demo: the photos of a demo order are not this queue's to upload (nor to track as stuck).
            event.target.result.filter((photo) => !isLiveDemoId(photo.order))
        );
        request.onerror = (event) => reject(event.target.error);
    });
}

async function getOrderByKey(key) {
    return new Promise((resolve, reject) => {
        if (!dbInstance) {
            reject(new Error('Database not initialized'));
            return;
        }
        const transaction = dbInstance.transaction('orders', 'readonly');
        const store = transaction.objectStore('orders');
        const request = store.get(key);

        request.onsuccess = (event) => resolve(event.target.result);
        request.onerror = (event) => reject(event.target.error);
    });
}

/** Drop the local record. Says nothing about WHY it went — the caller announces that. */
async function deletePhotoRecord(key) {
    return new Promise((resolve, reject) => {
        if (!dbInstance) {
            reject(new Error('Database not initialized'));
            return;
        }
        const transaction = dbInstance.transaction('photos', 'readwrite');
        const store = transaction.objectStore('photos');
        const request = store.delete(key);

        request.onsuccess = () => resolve();
        request.onerror = (event) => reject(event.target.error);
        transaction.onabort = (event) => reject(new Error(`Transaction aborted while deleting photo: ${event.target.error}`));
    });
}

async function markPhotoAsUploaded(key) {
    await deletePhotoRecord(key);
    // Let the UI know a queued photo left the local queue (the photo counter
    // shows server-confirmed + locally-pending, so it must refresh now).
    // `photoUploaded` means the photo REACHED S3 — analysisQueue listens for it and
    // launches the comparison. Never fire it for a photo that was thrown away.
    window.dispatchEvent(new CustomEvent('photoUploaded', { detail: { photoKey: key } }));
}

async function setPhotoOrphanCycles(key, orphanCycles) {
    return new Promise((resolve, reject) => {
        if (!dbInstance) {
            reject(new Error('Database not initialized'));
            return;
        }
        const transaction = dbInstance.transaction('photos', 'readwrite');
        const store = transaction.objectStore('photos');
        const getRequest = store.get(key);
        getRequest.onsuccess = (event) => {
            const photo = event.target.result;
            if (!photo) {
                resolve(); // already gone — nothing to track
                return;
            }
            photo.orphanCycles = orphanCycles;
            const putRequest = store.put(photo);
            putRequest.onsuccess = () => resolve();
            putRequest.onerror = (event) => reject(event.target.error);
        };
        getRequest.onerror = (event) => reject(event.target.error);
    });
}

async function updatePhotoUploadProgress(key, percentage, transferredBytes, totalBytes) {
    return new Promise((resolve, reject) => {
        if (!dbInstance) {
            reject(new Error('Database not initialized'));
            return;
        }
        const transaction = dbInstance.transaction('photos', 'readwrite');
        const store = transaction.objectStore('photos');
        
        // First get the existing photo record
        const getRequest = store.get(key);
        
        getRequest.onsuccess = (event) => {
            const photo = event.target.result;
            if (photo) {
                photo.percentageUploaded = percentage;
                photo.transferredBytes = transferredBytes;
                photo.totalBytes = totalBytes;
                const putRequest = store.put(photo);
                putRequest.onsuccess = () => resolve();
                putRequest.onerror = (event) => reject(event.target.error);
            } else {
                reject(new Error('Photo not found'));
            }
        };
        
        getRequest.onerror = (event) => reject(event.target.error);
    });
}

export function startBackgroundUploader(restaurantId, deviceId) {
    if (!restaurantId || !deviceId) {
        throw new Error('restaurantId and deviceId are required');
    }

    const uploadInterval = 15000; // 15 seconds
    let uploadTimeout;

    const scheduleNextUpload = () => {
        uploadTimeout = setTimeout(async () => {
            if (navigator.onLine) {
                try {
                    await uploadPendingPhotos(restaurantId, deviceId);
                } catch (error) {
                    logger.error('Error in background upload cycle', {error: error});
                }
            } else {
                logger.console('Device is offline. Skipping upload cycle.');
            }
            scheduleNextUpload();
        }, uploadInterval);
    };

    scheduleNextUpload();

    // Clean up function
    return () => {
        clearTimeout(uploadTimeout);
    };
}

export {uploadPendingPhotos};